import {
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Spacer,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { useState,useEffect } from "react";
import { TbMinus, TbPlus } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { scrollbarStyle } from "../../theme/scrollbarStyles";
import { ArrowLeft, Search } from "../icons/icons";
import LazyAvatar from "../lazy/LazyAvatar";
import { SafeArea } from "capacitor-plugin-safe-area";
import useMobile from "../../hooks/useMobile";


interface User {
  id: string;
  name: string;
  avatar: string | null;
  isSelected?: boolean;
}

interface UserSelectModalProps {
  title?: string;
  isMobile?: boolean;
  isOpen: boolean;
  onClose: () => void;
  isAdmin?: boolean;
  users: User[];
  onChange: (selectedUsers: User[]) => void;
  hideButtons?: boolean;

  customIcon?: React.ReactElement;
  customFooterButtons?: React.ReactElement;

  enableCheckIn?: boolean;
  checkedInIds?: Set<string>;
  onCheckIn?: (userId: string, checked: boolean) => void;
}

const UserSelectModal = ({
  isMobile: isMobileProp,
  isOpen,
  onClose,
  hideButtons,
  users,
  isAdmin,
  customIcon,
  title = "All users",
  enableCheckIn,
  checkedInIds,
  onCheckIn,
}: UserSelectModalProps) => {
  const isMobileDetected = useMobile(768);
  const isMobile = isMobileProp ?? isMobileDetected;
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const [statusBarHeight, setStatusBarHeight] = useState<number>(0);

  useEffect(() => {
    SafeArea.getStatusBarHeight().then(({ statusBarHeight }) => {
      setStatusBarHeight(statusBarHeight);
    });
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={isMobile ? "full" : "md"}>
      <ModalOverlay />
      <ModalContent p="0">
      <Spacer h={statusBarHeight + "px"}  userSelect="none"/>
        <ModalBody p="20px">
          {isMobile ? (
            <Box w="100%" p="20px 0" zIndex={1} pos="relative">
              <Center pos="absolute" w="100%" h="100%" top="0">
                <Text variant="title" fontSize="lg">
                  {title}
                </Text>
              </Center>
              <IconButton
                minW="32px"
                h="32px"
                variant="light"
                aria-label="back"
                onClick={onClose}
                icon={
                  <Icon width="20px" height="19px" viewBox="0 0 23 20">
                    <ArrowLeft color="#171C22" />
                  </Icon>
                }
              />
            </Box>
          ) : (
            <Text mb="20px" variant="title">
              {title}
            </Text>
          )}

          <InputGroup>
            <InputLeftElement>
              <Icon width="18" height="18" viewBox="0 0 18 18">
                <Search color="#7E8B9A" />
              </Icon>
            </InputLeftElement>
            <Input
              w="100%"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              variant="filled"
              placeholder="Search or start a new chat..."
            />
          </InputGroup>
          <Box
            h={isMobile ? "calc(100vh - 300px)" : "400px"}
            overflowY="auto"
            sx={scrollbarStyle}
            mr="10px"
          >
            {users?.length > 0 &&
              users
                .filter((user) =>
                  user.name.toLowerCase().includes(search.toLowerCase())
                )
                .map((user, i) => {
                  return (
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      p="10px 0"
                      key={i}
                      borderRadius="10px"
                    >
                      <Flex
                        alignItems="center"
                        gap="16px"
                        onClick={() =>
                          isAdmin
                            ? navigate("/admin/profile?id=" + user.id)
                            : navigate("/user/profile?id=" + user.id)
                        }
                        cursor="pointer"
                      >
                        <LazyAvatar
                          src={user.avatar ?? ""}
                          width={41}
                          height={41}
                          border="1px solid #7E8B9A"
                        />
                        <Text variant="title" fontSize="lg">
                          {user.name}
                        </Text>
                      </Flex>
                      <Flex gap="8px" alignItems="center">
                        {enableCheckIn && (
                          <Checkbox
                            isChecked={checkedInIds?.has(user.id) ?? false}
                            onChange={() => onCheckIn?.(user.id, !(checkedInIds?.has(user.id) ?? false))}
                            colorScheme="green"
                            size="lg"
                          />
                        )}
                        {!isAdmin && (
                          <IconButton
                            aria-label="add or remove user"
                            variant="light"
                            icon={
                              customIcon ?? (
                                <Icon as={user.isSelected ? TbMinus : TbPlus} />
                              )
                            }
                            onClick={() => {
                              navigate("/user/messages?to=" + user.id);
                            }}
                          />
                        )}
                      </Flex>
                    </Flex>
                  );
                })}
          </Box>
          {!hideButtons && (
            <Flex gap="12px" w="100%">
              <Button variant="light" size="sm" w="50%" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="heavy"
                size="sm"
                w="50%"
                onClick={() => {
                  navigate(
                    "/user/messages?to=" +
                      users.map((user) => user.id).join(",")
                  );
                  onClose();
                }}
              >
                Send group message
              </Button>
            </Flex>
          )}
          {/*customFooterButtons ? customFooterButtons : <></>*/}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default UserSelectModal;
